import { CheckMarkIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { acceptEnrollmentCse } from '../../api/enrollments/accept-cse'
import { rejectEnrollmentCse } from '../../api/enrollments/reject-cse'
import PendingLoader from '../../components/elements/loading/pending-loader'
import { extractErrorMessage, showAxiosError } from '../../lib/utils/functions/misc.functions'

type Props = {
  mode: 'accept' | 'reject'
}

type CseValidationMessageProps = {
  title: string
  description: string
  isSuccess?: boolean
}

function CseValidationMessage({
  title,
  description,
  isSuccess = false
}: CseValidationMessageProps) {
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-white-light p-5">
      <div className="flex w-full max-w-[520px] flex-col items-center gap-5 rounded-lg bg-white p-8 text-center shadow-sm">
        {isSuccess ? <CheckMarkIcon className="h-10 w-10 text-green" /> : null}

        <div className="flex flex-col gap-2">
          <Typography variant="h2">{title}</Typography>
          <Typography variant="body" className="text-grey">
            {description}
          </Typography>
        </div>
      </div>
    </div>
  )
}

export function EnrollmentCseValidationPage({ mode }: Props) {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const [receivedResponse, setReceivedResponse] = useState(false)
  const token = searchParams.get('token')
  const enrollmentId = id ? parseInt(id) : undefined

  const { mutate, error } = useMutation({
    mutationFn: mode === 'accept' ? acceptEnrollmentCse : rejectEnrollmentCse,
    onSuccess: () => setReceivedResponse(true),
    onError: showAxiosError
  })

  useEffect(() => {
    if (!enrollmentId || !token) return
    mutate({ enrollmentId, token })
  }, [enrollmentId, mutate, token])

  if (!enrollmentId || !token)
    return (
      <CseValidationMessage
        title="Lien invalide"
        description="Le lien de validation CSE est incomplet."
      />
    )

  if (error)
    return (
      <CseValidationMessage
        title="Validation impossible"
        description={extractErrorMessage(error) ?? 'Une erreur est survenue'}
      />
    )

  if (receivedResponse)
    return (
      <CseValidationMessage
        title="Validation réussie"
        description="La validation CSE a été effectuée avec succès."
        isSuccess
      />
    )

  return <PendingLoader text="Validation CSE en cours..." />
}
